import React from "react";
import { Badge } from "@/components/ui/badge";
import { FileText, TrendingUp } from "lucide-react";
import DashboardWidget from "@/components/DashboardWidget";

interface AnalyticsStats {
  popularPages: Array<{ page: string; views: number }>;
  totalPageViews: number;
}

interface PopularPagesWidgetProps {
  stats: AnalyticsStats | null;
}

export const PopularPagesWidget: React.FC<PopularPagesWidgetProps> = ({
  stats,
}) => {
  const getPageDisplayName = (page: string) => {
    const pageNames: Record<string, string> = {
      "/": "Home Page",
      "/ride": "Ride Service",
      "/drive": "Drive with Us",
      "/about": "About Us",
      "/help": "Help & Support",
      "/business": "Business Services",
      "/app": "Mobile App",
      "/airports": "Airports",
      "/taxi": "Cities",
      "/popular-routes": "Popular Routes",
      "/reserve": "Make Reservation",
    };

    // Handle dynamic routes
    if (page.startsWith("/airports/")) {
      const airportCode = page.split("/")[2];
      return `${airportCode?.toUpperCase()} Airport`;
    }

    if (page.startsWith("/taxi/")) {
      const cityName = page.split("/")[2];
      return `${cityName?.charAt(0).toUpperCase()}${cityName?.slice(1)} City`;
    }

    if (page.startsWith("/taxis/")) {
      const townName = page.split("/")[2];
      return `${townName?.charAt(0).toUpperCase()}${townName?.slice(1)} Town`;
    }

    return pageNames[page] || page;
  };

  const getPageIcon = (page: string) => {
    if (page === "/") return "🏠";
    if (page.includes("/airports/")) return "✈️";
    if (page.includes("/taxi/") || page.includes("/taxis/")) return "🏙️";
    if (page === "/ride") return "🚗";
    if (page === "/drive") return "👨‍💼";
    if (page === "/app") return "📱";
    if (page === "/business") return "💼";
    if (page === "/help") return "❓";
    if (page === "/about") return "ℹ️";
    return "📄";
  };

  return (
    <DashboardWidget
      id="popular-pages"
      title="Popular Pages"
      headerActions={<FileText className="h-5 w-5" />}
    >
      {stats?.popularPages?.length ? (
        <div className="space-y-3">
          {stats.popularPages.slice(0, 8).map((page, index) => {
            const percentage =
              stats.totalPageViews > 0
                ? ((page.views / stats.totalPageViews) * 100).toFixed(1)
                : "0";
            const displayName = getPageDisplayName(page.page);
            const icon = getPageIcon(page.page);

            return (
              <div
                key={page.page}
                className="flex items-center justify-between p-3 border rounded-lg hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-6 h-6 bg-blue-600 text-white rounded-full text-xs font-bold">
                    {index + 1}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{icon}</span>
                    <div>
                      <div className="font-medium text-sm">{displayName}</div>
                      <div className="text-xs text-gray-500">{page.page}</div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-semibold text-sm">
                      {page.views.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">
                      {percentage}% of total
                    </div>
                  </div>
                  <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                    {page.views}
                  </Badge>
                </div>
              </div>
            );
          })}

          {stats.popularPages.length > 8 && (
            <div className="pt-3 border-t">
              <div className="text-sm text-gray-600 text-center">
                Showing top 8 of {stats.popularPages.length} pages
              </div>
            </div>
          )}

          <div className="pt-3 border-t">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Total Page Views:</span>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-600" />
                <span className="font-semibold">
                  {stats.totalPageViews?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No page view data available yet</p>
          <p className="text-sm text-gray-400">
            Popular pages will appear as visitors browse your site
          </p>
        </div>
      )}
    </DashboardWidget>
  );
};

export default PopularPagesWidget;
